import FlagStatus from 'owa-service/lib/contract/FlagStatus';
import getConversationProperty from './getConversationProperty';
import getCurrentSelectedTableView from '../utils/getSelectedTableView';
import getItemProperty from './getItemProperty';
import isItemView from '../utils/isItemView';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import TableView from '../store/schema/TableView';
import { MailListDataPropertyEnum } from './MailListDataPropertyEnum';

/**
 * Get the specified property from the row
 * @param rowId conversation/item id
 * @param tableView the table that contains the row data
 * @param propertyType to retrieve on the row data
 */
export const getRowDataProperty = (
    rowId: string,
    tableView: TableView,
    propertyType: MailListDataPropertyEnum
) => {
    if (isItemView(tableView)) {
        return getItemProperty(rowId, propertyType);
    }

    return getConversationProperty(rowId, tableView, propertyType);
};

/**
 * Get the lastDeliveryTimeStamp of a row
 * @param rowId rowId of row
 * @param tableView the table that contains the row data
 * @return the lastDeliveryTimeStamp
 */
export const getLastDeliveryTimeStamp = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.LastDeliveryTimeStamp) as string;

/**
 * Get the LastDeliveryOrRenewTimeStamp for a row
 * @return the lastDeliveryTimeStamp
 */
export const getLastDeliveryOrRenewTimeStamp = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) =>
    getRowDataProperty(
        rowId,
        tableView,
        MailListDataPropertyEnum.LastDeliveryOrRenewTimeStamp
    ) as string;

export const getSubject = (rowId: string, tableView: TableView = getCurrentSelectedTableView()) =>
    getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.Subject) as string;

export const getParentFolderId = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.ParentFolderId) as string;

export const getUniqueRecipients = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.UniqueRecipients) as string[];

export const getUniqueSenders = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.UniqueSenders) as string[];

export const getLastSender = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) =>
    getRowDataProperty(
        rowId,
        tableView,
        MailListDataPropertyEnum.LastSender
    ) as SingleRecipientType;

export const getUnreadCount = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.UnreadCount) as number;

export const getPreviewText = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.PreviewText) as string;

export function getIsPinned(
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
): boolean {
    return getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.IsPinned) as boolean;
}

export function getFlagStatus(
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
): FlagStatus {
    return getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.FlagStatus) as FlagStatus;
}

/**
 * Get the LastModifiedTime for a row
 */
export function getLastModifiedTime(rowId: string, tableView: TableView) {
    return getRowDataProperty(
        rowId,
        tableView,
        MailListDataPropertyEnum.LastModifiedTime
    ) as string;
}

/**
 * Get the shouldShowRSVP property for a row
 */
export const getShouldShowRSVP = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => !!getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.ShouldShowRSVP);

/**
 * Get the rsvpUnitAriaDetails property for a row
 */
export const getRsvpUnitAriaDetailsForMailListRow = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, MailListDataPropertyEnum.RsvpUnitAriaDetails) as string;
